import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface BotIdPopupProps {
    onClose: () => void;
    isDashboard: boolean;
}

export const BotIdPopup = ({ onClose, isDashboard }: BotIdPopupProps) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const content = (
        <div ref={ref} className="relative text-foreground cursor-auto" onMouseDown={e => e.stopPropagation()}>
            <p className="text-xs font-bold text-foreground mb-1">Where is my Bot ID?</p>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                Your Bot ID is shown in the <strong>Deploy & Share</strong> step <strong>4</strong>. Open any of your bots from the dashboard, then go to the last step.
            </p>

            <div className="flex items-center gap-2 p-2.5 bg-muted/40 rounded-lg border border-border/60 mb-3">
                <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0 text-primary text-xs font-bold">
                    ID
                </div>
                <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">BOT_ID</p>
                    <p className="text-xs font-mono text-foreground/60">abc123xyz...</p>
                </div>
            </div>

            <Link
                to={isDashboard ? "/dashboard" : "/auth?redirect=/dashboard"}
                onClick={onClose}
                className="flex items-center justify-between w-full px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
            >
                {isDashboard ? "Go to Dashboard" : "Log in to Dashboard"}
                <ArrowRight className="w-3.5 h-3.5" />
            </Link>
        </div>
    );

    return (
        <AnimatePresence>
            {/* Mobile: fixed bottom sheet */}
            <motion.div
                key="mobile-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="sm:hidden fixed inset-0 z-50 bg-black/40 flex items-end"
                onMouseDown={onClose}
            >
                <motion.div
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                    className="w-full bg-popover border-t border-border rounded-t-2xl p-5 pb-8"
                    onMouseDown={e => e.stopPropagation()}
                >
                    <div className="w-10 h-1 bg-border rounded-full mx-auto mb-4" />
                    {content}
                </motion.div>
            </motion.div>

            {/* Desktop: tooltip below badge, left-aligned */}
            <motion.div
                key="desktop-tooltip"
                initial={{ opacity: 0, y: -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.97 }}
                transition={{ duration: 0.18 }}
                className="hidden sm:block absolute z-50 top-full left-0 mt-3 w-72 bg-popover border border-border rounded-xl shadow-xl p-4 text-left cursor-default transform translate-x-[-10px]"
                onMouseDown={e => e.stopPropagation()}
            >
                {/* Arrow pointing up */}
                <div className="absolute -top-[6px] left-[26px] w-3 h-3 bg-popover border-l border-t border-border rotate-45" />

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                    type="button"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                </button>

                {content}
            </motion.div>
        </AnimatePresence>
    );
};
